import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { API_URL } from "@/lib/config";
import {
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
  Cpu,
  Bot,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SiteShell } from "@/components/SiteShell";
import { fetchAuthorProfile, getAuthorSocial } from "@/lib/author";

export const Route = createFileRoute("/contact")({
  loader: () => fetchAuthorProfile(),
  head: ({ loaderData }) => ({
    meta: [
      { title: `Contact ${loaderData?.name ?? "the Author"} | BuildWithJIT` },
      {
        name: "description",
        content:
          loaderData?.contact_seo_description ??
          "Contact the BuildWithJIT team about AI systems architecture and engineering consulting.",
      },
      {
        name: "robots",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      {
        name: "googlebot",
        content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { property: "og:site_name", content: "BuildWithJIT" },
      {
        property: "og:title",
        content: `Contact ${loaderData?.name ?? "the Author"} | BuildWithJIT`,
      },
      {
        property: "og:description",
        content:
          loaderData?.contact_seo_description ??
          "Contact the BuildWithJIT team about AI systems architecture and engineering consulting.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://buildwithjit.com/contact" },
      { property: "og:image", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:secure_url", content: "https://buildwithjit.com/og-share.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      {
        property: "og:image:alt",
        content: `Contact ${loaderData?.name ?? "the Author"}: BuildWithJIT`,
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@buildwithjit" },
      { name: "twitter:creator", content: "@buildwithjit" },
      {
        name: "twitter:title",
        content: `Contact ${loaderData?.name ?? "the Author"} | BuildWithJIT`,
      },
      {
        name: "twitter:description",
        content:
          loaderData?.contact_seo_description ??
          "Contact the BuildWithJIT team about AI systems architecture and engineering consulting.",
      },
      { name: "twitter:image", content: "https://buildwithjit.com/og-share.png" },
    ],
    links: [
      { rel: "canonical", href: "https://buildwithjit.com/contact" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/icon-48.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/icon-96.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/icon-192.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "shortcut icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const profile = Route.useLoaderData();
  const social = getAuthorSocial(profile);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("Work With Me / Architecture Consulting");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setSubmitting(true);
    setErrorMessage("");
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          topic,
          message: message.trim(),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Server returned error (${res.status})`);
      }
      setSubmitted(true);
    } catch (err: unknown) {
      console.error("Contact submission error:", err);
      const msg = err instanceof Error ? err.message : "Could not send message. Please try again.";
      setErrorMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setName("");
    setEmail("");
    setTopic("Work With Me / Architecture Consulting");
    setMessage("");
    setErrorMessage("");
    setSubmitted(false);
  };

  return (
    <SiteShell social={social}>
      <div className="contact-page">
        {/* Page Hero Header */}
        <section className="contact-hero">
          <div className="container-wide">
            <div className="eyebrow">
              <span className="eyebrow-line" /> GET IN TOUCH / COLLABORATION
            </div>
            <h1>
              Let’s build something <em>together.</em>
            </h1>
            <p>
              Whether you want to discuss production AI architectures, need help designing a RAG
              pipeline or voice agent, or have technical questions about my articles, I’d love to
              connect.
            </p>
          </div>
        </section>

        {/* Main Content Layout */}
        <section className="contact-body">
          <div className="container-wide contact-grid">
            {/* Left: Contact Form */}
            <div className="contact-form-container">
              {submitted ? (
                <div className="contact-success-card">
                  <div className="success-icon-wrap">
                    <CheckCircle2 size={40} className="success-icon" />
                  </div>
                  <h2>Message received!</h2>
                  <p>
                    Thank you, <strong>{name}</strong>. Your note regarding <em>{topic}</em> has
                    landed safely in my inbox. I review every message personally and will follow up
                    at <strong>{email}</strong> shortly.
                  </p>
                  <Button onClick={handleReset} variant="outline" className="mt-4">
                    Send another message
                  </Button>
                </div>
              ) : (
                <div className="contact-form-card">
                  <div className="form-card-header">
                    <div className="section-kicker">DIRECT INQUIRY</div>
                    <h2>How can I help you?</h2>
                    <p>
                      Fill out the fields below and I will get back to you within 24 to 48 hours.
                    </p>
                  </div>

                  {errorMessage && (
                    <div className="p-3 mb-4 text-sm text-destructive bg-destructive/10 rounded-lg border border-destructive/20 flex items-center gap-2">
                      <HelpCircle size={16} /> {errorMessage}
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row-dual">
                      <label className="form-label">
                        <span>Your Name</span>
                        <Input
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          className="contact-input"
                        />
                      </label>

                      <label className="form-label">
                        <span>Email Address</span>
                        <Input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="contact-input"
                        />
                      </label>
                    </div>

                    <label className="form-label">
                      <span>Inquiry Topic</span>
                      <select
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        className="contact-select"
                        aria-label="Inquiry Topic"
                      >
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Editorial & Story Feedback">
                          Editorial & Story Feedback
                        </option>
                        <option value="Partnership & Collaboration">
                          Partnership & Collaboration
                        </option>
                        <option value="Story Pitch / Contributing">
                          Story Pitch / Contributing
                        </option>
                        <option value="Press & Media">Press & Media</option>
                      </select>
                    </label>

                    <label className="form-label">
                      <span>Your Message</span>
                      <textarea
                        required
                        rows={6}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Tell us what's on your mind, what you're building, or how we can collaborate…"
                        className="contact-textarea"
                      />
                    </label>

                    <Button
                      type="submit"
                      size="lg"
                      className="contact-submit-btn"
                      disabled={submitting}
                    >
                      {submitting ? (
                        "Sending note…"
                      ) : (
                        <>
                          Send message <Send size={16} />
                        </>
                      )}
                    </Button>
                  </form>
                </div>
              )}
            </div>

            {/* Right: Direct Information & Channels */}
            <div className="contact-info-column">
              <div className="info-card">
                <div className="info-card-header">
                  <div className="section-kicker">DIRECT CHANNELS</div>
                  <h3>Connect directly</h3>
                </div>

                <div className="info-items">
                  <div className="info-item">
                    <div className="info-icon">
                      <Mail size={19} />
                    </div>
                    <div>
                      <strong>General & Partnerships</strong>
                      <a href="mailto:info@buildwithjit.com" className="info-link">
                        info@buildwithjit.com
                      </a>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <Mail size={19} />
                    </div>
                    <div>
                      <strong>Support & Reader Help</strong>
                      <a href="mailto:help@buildwithjit.com" className="info-link">
                        help@buildwithjit.com
                      </a>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <Mail size={19} />
                    </div>
                    <div>
                      <strong>Founder & Consulting</strong>
                      <a href={`mailto:${profile.email}`} className="info-link">
                        {profile.email}
                      </a>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <MapPin size={19} />
                    </div>
                    <div>
                      <strong>Headquarters</strong>
                      <span>{profile.location} & Remote Worldwide</span>
                    </div>
                  </div>

                  <div className="info-item">
                    <div className="info-icon">
                      <Clock size={19} />
                    </div>
                    <div>
                      <strong>Expected Response</strong>
                      <span>Within 24 to 48 hours</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Editorial / Quick FAQ */}
              <div className="info-card faq-card">
                <div className="info-card-header">
                  <div className="section-kicker">
                    <HelpCircle size={14} className="inline mr-1" /> FREQUENTLY ASKED
                  </div>
                  <h3>Good to know</h3>
                </div>

                <div className="faq-list">
                  <div className="faq-item">
                    <strong>Can I pitch an article?</strong>
                    <p>
                      Yes! We welcome deep-dive technical pitches and architectures on AI
                      automation, RAG pipelines, voice AI agents, and production systems
                      engineering.
                    </p>
                  </div>

                  <div className="faq-item">
                    <strong>Are partnerships available?</strong>
                    <p>
                      We partner with tools, teams, and publications that align with our focus on
                      craftsmanship and curiosity.
                    </p>
                  </div>
                </div>

                <div className="faq-footer">
                  <Link to="/" hash="latest" className="explore-link">
                    Explore latest articles <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}
